import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { PrimaryButton, SecondaryButton } from '../ui';
import { aggregatorApi } from '../../api/aggregatorApi';
import SettlementEditDrawer from './SettlementEditDrawer';

export interface EntrySettledDetails {
  settlementId: number;
  allocationCount?: number | null;
  periodFrom?: string | null;
  periodTo?: string | null;
  channelId?: number | null;
  channelName?: string | null;
  entryDate?: string | null;
}

interface SettlementRecoveryDialogProps {
  open: boolean;
  details: EntrySettledDetails | null;
  onClose: () => void;
  /** Called after the user successfully edits or deletes the settlement. */
  onResolved: () => void;
}

const SettlementRecoveryDialog: React.FC<SettlementRecoveryDialogProps> = ({
  open,
  details,
  onClose,
  onResolved,
}) => {
  const { theme } = useTheme();
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!details) return null;

  const allocationCount = details.allocationCount ?? 1;
  const isSole = allocationCount <= 1;
  const coversLine =
    details.periodFrom && details.periodTo && details.periodFrom !== details.periodTo
      ? `${details.periodFrom} → ${details.periodTo}`
      : details.periodFrom || details.entryDate || '';

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await aggregatorApi.deleteSettlement(details.settlementId);
      toast.success('Settlement deleted. Entries returned to pending.');
      onResolved();
      onClose();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e?.response?.data?.message || 'Failed to delete settlement');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open && !editDrawerOpen} onClose={deleting ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Entry locked by settlement #{details.settlementId}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2">
              You're trying to edit revenue for{' '}
              <strong>
                {details.channelName || 'this channel'}
                {details.entryDate ? ` on ${details.entryDate}` : ''}
              </strong>
              . That entry is already part of a recorded settlement, so its reported amount is
              frozen on the settlement record.
            </Typography>

            {isSole ? (
              <Typography variant="body2" sx={{ color: theme.gray[700] }}>
                Settlement #{details.settlementId}
                {coversLine ? ` covers ${coversLine}` : ''} and this is the only entry it
                covers. Delete the settlement and we'll re-record it after you finish editing.
              </Typography>
            ) : (
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 1,
                  backgroundColor: `${theme.danger}10`,
                  border: `1px solid ${theme.danger}40`,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  ⚠ Settlement covers {allocationCount} entries
                  {coversLine ? ` across ${coversLine}` : ''}.
                </Typography>
                <Typography variant="caption" sx={{ color: theme.gray[700] }}>
                  Editing or deleting it will affect every covered day. Open the settlement
                  editor to adjust just the period and amount, or delete the whole settlement
                  and re-record it.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <SecondaryButton onClick={onClose} disabled={deleting}>
            Cancel
          </SecondaryButton>
          {!isSole && (
            <SecondaryButton onClick={() => setEditDrawerOpen(true)} disabled={deleting}>
              Open settlement editor
            </SecondaryButton>
          )}
          <PrimaryButton
            onClick={handleDelete}
            disabled={deleting}
            sx={{ backgroundColor: theme.danger, '&:hover': { backgroundColor: theme.danger } }}
          >
            {deleting ? 'Deleting…' : isSole ? 'Delete settlement' : 'Delete entire settlement'}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <SettlementEditDrawer
        open={editDrawerOpen}
        settlementId={details.settlementId}
        onClose={() => setEditDrawerOpen(false)}
        onSaved={() => {
          setEditDrawerOpen(false);
          onResolved();
          onClose();
        }}
      />
    </>
  );
};

export default SettlementRecoveryDialog;
